import React, { useState, useEffect, useCallback } from 'react';
import { Bell, CheckCheck, Clock, AlertTriangle, MessageSquare, Globe, BarChart, BellRing, BellOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import usePushNotifications from '@/hooks/usePushNotifications';

const API_URL = '';

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  // Push notification hook
  const { isSupported, isSubscribed, subscribe, unsubscribe, permission } = usePushNotifications();

  // Check both 'user' and 'currentUser' keys for compatibility
  const user = JSON.parse(localStorage.getItem('user') || localStorage.getItem('currentUser') || '{}');
  // Show notifications for teachers AND admin roles
  const adminRoles = ['principal', 'deputy_principal', 'academic_principal', 'coordinator', 'admin'];
  const isTeacher = user?.role === 'teacher';
  const isAdmin = adminRoles.includes(user?.role);
  const showNotifications = isTeacher || isAdmin;
  const userId = user?.id;

  const fetchNotifications = useCallback(async () => {
    if (!userId) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/notifications/my-notifications?user_id=${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
        setUnreadCount(data.filter(n => !n.is_read).length);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const fetchUnreadCount = useCallback(async () => {
    if (!userId) return;
    
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/notifications/unread-count?user_id=${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [userId]);

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      await fetch(`${API_URL}/api/notifications/mark-read/${notificationId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      await fetch(`${API_URL}/api/notifications/mark-all-read?user_id=${userId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Fetch unread count on mount and poll periodically
  useEffect(() => {
    if (!showNotifications) return;
    
    fetchUnreadCount();
    
    // Poll for new notifications every 60 seconds (30 seconds for admins to get faster alerts)
    const pollInterval = isAdmin ? 30000 : 60000;
    const interval = setInterval(fetchUnreadCount, pollInterval);
    return () => clearInterval(interval);
  }, [showNotifications, isAdmin, fetchUnreadCount]);

  // Fetch full notifications when popover opens
  useEffect(() => {
    if (open && showNotifications) {
      fetchNotifications();
    }
  }, [open, showNotifications, fetchNotifications]);

  const getNotificationIcon = (type, priority) => {
    if (priority === 'high' || priority === 'urgent') {
      return <AlertTriangle className="h-4 w-4 text-orange-500" />;
    }
    
    switch (type) {
      case 'marks_reminder':
        return <Clock className="h-4 w-4 text-blue-500" />;
      case 'deadline_warning':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'remote_signin':
        return <Globe className="h-4 w-4 text-orange-500" />;
      case 'daily_summary':
        return <BarChart className="h-4 w-4 text-purple-500" />;
      case 'ia_draft_submission':
        return <MessageSquare className="h-4 w-4 text-emerald-500" />;
      case 'ia_final_submission':
        return <CheckCheck className="h-4 w-4 text-emerald-600" />;
      default:
        return <MessageSquare className="h-4 w-4 text-gray-500" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    
    return date.toLocaleDateString();
  };

  // Don't render for users who shouldn't see notifications
  if (!showNotifications) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative text-muted-foreground hover:text-primary"
          data-testid="notification-bell"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-[10px] bg-red-500"
              data-testid="notification-count"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between p-4 border-b">
          <h4 className="font-semibold">Notifications</h4>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-xs h-7"
              onClick={markAllAsRead}
            >
              <CheckCheck className="h-3 w-3 mr-1" />
              Mark all read
            </Button>
          )}
        </div>
        
        {/* Push Notifications Toggle - only for admins */}
        {isAdmin && isSupported && (
          <div className="flex items-center justify-between p-3 border-b bg-muted/30">
            <div className="flex items-center gap-2">
              {isSubscribed ? (
                <BellRing className="h-4 w-4 text-green-600" />
              ) : (
                <BellOff className="h-4 w-4 text-gray-400" />
              )}
              <span className="text-xs">Browser Alerts</span>
            </div>
            <Switch
              checked={isSubscribed}
              onCheckedChange={async (checked) => {
                if (checked) {
                  const sub = await subscribe();
                  if (sub) {
                    toast.success('Browser notifications enabled');
                  } else if (permission === 'denied') {
                    toast.error('Please enable notifications in browser settings');
                  }
                } else {
                  await unsubscribe();
                  toast.info('Browser notifications disabled');
                }
              }}
              data-testid="push-notification-toggle"
            />
          </div>
        )}
        
        <ScrollArea className="h-[300px]">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500 p-4">
              <Bell className="h-8 w-8 mb-2 opacity-50" />
              <p className="text-sm">No notifications</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={cn(
                    "p-3 hover:bg-muted/50 cursor-pointer transition-colors",
                    !notification.is_read && "bg-blue-50"
                  )}
                  onClick={() => !notification.is_read && markAsRead(notification.id)}
                  data-testid={`notification-item-${notification.id}`}
                >
                  <div className="flex gap-3">
                    <div className="mt-0.5">
                      {getNotificationIcon(notification.type, notification.priority)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium truncate">
                          {notification.title}
                        </p>
                        {!notification.is_read && (
                          <div className="h-2 w-2 rounded-full bg-blue-500 flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-xs text-gray-600 line-clamp-2 mt-0.5">
                        {notification.message}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {formatDate(notification.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
